import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from '../../context/GlobalContext';

const LanguageSelector = ({ options, displayFlag, defaultLanguage, tabIndex }) => {
  const { i18n, t } = useTranslation();
  const { changeLanguage: changeGlobalLanguage } = useGlobalContext();
  const [selectedLanguage, setSelectedLanguage] = useState({
    code: defaultLanguage,
    name: defaultLanguage
  });
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const savedLang = sessionStorage.getItem('lang');
    if (savedLang && options) {
      const found = options.find(lang =>
        lang.code.toLowerCase() === savedLang.toLowerCase()
      );
      if (found) {
        setSelectedLanguage(found);
        i18n.changeLanguage(found.code);
      }
    } else if (defaultLanguage && options) {
      const defaultLang = options.find(lang =>
        lang.code.toLowerCase() === defaultLanguage.toLowerCase()
      );
      if (defaultLang) {
        setSelectedLanguage(defaultLang);
        i18n.changeLanguage(defaultLang.code);
        sessionStorage.setItem('lang', defaultLang.code);
      }
    }
  }, [defaultLanguage, options, i18n]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const chooseLanguage = async (currentId, name) => {
    try {
      await i18n.changeLanguage(currentId);
      setSelectedLanguage({ code: currentId, name });
      sessionStorage.setItem('lang', currentId);

      if (changeGlobalLanguage) {
        changeGlobalLanguage(currentId);
      }

      setDropdownOpen(false);
    } catch (error) {
      console.error('Erreur lors du changement de langue:', error);
    }
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    setDropdownOpen(!dropdownOpen);
  };

  if (!options || options.length < 2) return null;
  
  return (
    <nav
      ref={containerRef}
      id="navbar"
      className="navbar-collapse collapse navbar-right languageSelector"
      aria-label={t('Sélection de la langue')}
    >
      <ul className="nav navbar-nav" role="menubar">
        <li 
          className={`dropdown languages ${dropdownOpen ? 'open' : ''}`} 
          role="none"
        >
          <a
            className="dropdown-toggle"
            data-toggle="dropdown"
            role="menuitem"
            href="#"
            tabIndex="0"
            aria-haspopup="true"
            aria-expanded={dropdownOpen ? 'true' : 'false'}
            onClick={(e) => {
              e.preventDefault();
              toggleDropdown(e)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleDropdown(e);
              }
            }}
            aria-label={t('Sélectionner la langue')}
          >
            {displayFlag && (
              <img 
                id="imgNavSel" 
                src={`/assets/images/flags/${selectedLanguage.code}.png`} 
                alt={`${selectedLanguage.name} flag`}
                className="icon-small" 
              />
            )}
            <span id="lanNavSel">
              {selectedLanguage.name.toUpperCase()}
            </span>
            <span className="caret" aria-hidden="true"></span>
          </a>
          <ul className="dropdown-menu" role="menu" aria-label={t('Options de langue disponibles')}>
            {options.map(lang => (
              <li key={lang.code} role="none">
                <a
                  href="#"
                  tabIndex="0"
                  onClick={(e) => {
                    e.preventDefault();
                    chooseLanguage(lang.code, lang.name)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      chooseLanguage(lang.code, lang.name)
                    }
                  }}
                  className={`language ${selectedLanguage.code === lang.code ? 'active' : ''}`}
                  id={lang.code}
                  role="menuitem"
                  aria-label={t('Changer la langue en {{language}}', { language: lang.name })}
                  aria-current={selectedLanguage.code === lang.code ? 'true' : 'false'}
                >
                  {displayFlag && (
                    <img 
                      id={`image${lang.code}`}
                      src={`/assets/images/flags/${lang.code}.png`} 
                      alt={`${lang.name} flag`}
                      className="icon-small" 
                    />
                  )}
                  <span id={`nav${lang.code}`} className="capitalize">
                    {lang.name}
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </li>
      </ul>
    </nav>
  );
};

export default LanguageSelector;